import React, { useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const PreLaminationELRework = ({ formData, onFormDataChange }) => {
    const { preLaminationEL = [], reWork = [], observationsEL = [], observationsRework = [], nonConformance = '' } = formData;

    useEffect(() => {
        // Destructure formData to get initial values
        const { preLaminationEL = [], reWork = [], observationsEL = [], observationsRework = [], nonConformance = '' } = formData;
    
        // Check if formData already has prefilled data
        const hasFormData =
            preLaminationEL.length > 0 ||
            reWork.length > 0 ||
            observationsEL.length > 0 ||
            observationsRework.length > 0 ||
            nonConformance.trim() !== '';
    
        if (!hasFormData) {
            // If no formData, check metadata and inspectionData
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));
            const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));
    
            let prefilledPreLaminationEL = [];
            let prefilledReWork = [];
            let prefilledObservationsEL = [];
            let prefilledObservationsRework = [];
            let prefilledNonConformance = '';
    
            // Check metadata for prefilled data
            if (metadata && metadata.pre && metadata.pre.PreLaminationELRework_pre) {
                const { preLaminationEL: metaPreLaminationEL, reWork: metaReWork } = metadata.pre.PreLaminationELRework_pre;
                prefilledPreLaminationEL = metaPreLaminationEL || [];
                prefilledReWork = metaReWork || [];
                prefilledObservationsEL = metaPreLaminationEL.map(() => ({ observation1: '', observation2: '' }));
                prefilledObservationsRework = metaReWork.map(() => ({ observation1: '', observation2: '' }));
            }
    
            // Check inspectionData for prefilled data (overrides metadata if present)
            if (
                inspectionData &&
                inspectionData.PreLamination &&
                inspectionData.PreLamination.PreLaminationELRework_pre
            ) {
                const {
                    preLaminationEL: inspectionPreLaminationEL,
                    reWork: inspectionReWork,
                    nonConformance: inspectionNonConformance,
                } = inspectionData.PreLamination.PreLaminationELRework_pre;
    
                prefilledPreLaminationEL = inspectionPreLaminationEL || [];
                prefilledReWork = inspectionReWork || [];
                prefilledObservationsEL = inspectionPreLaminationEL.map((item) => ({
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));
                prefilledObservationsRework = inspectionReWork.map((item) => ({
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));
                prefilledNonConformance = inspectionNonConformance || '';
            }
    
            // Update formData only if prefilled data exists
            if (
                prefilledPreLaminationEL.length > 0 ||
                prefilledReWork.length > 0 ||
                prefilledObservationsEL.length > 0 ||
                prefilledObservationsRework.length > 0 ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    preLaminationEL: prefilledPreLaminationEL,
                    reWork: prefilledReWork,
                    observationsEL: prefilledObservationsEL,
                    observationsRework: prefilledObservationsRework,
                    nonConformance: prefilledNonConformance,
                });
            }
        }
    }, [formData, onFormDataChange]);

    const handleObservationChangeEL = (index, field, value) => {
        const updatedObservations = [...observationsEL];
        updatedObservations[index][field] = value;
        onFormDataChange({ ...formData, observationsEL: updatedObservations });
    };

    const handleObservationChangeRework = (index, field, value) => {
        const updatedObservations = [...observationsRework];
        updatedObservations[index][field] = value;
        onFormDataChange({ ...formData, observationsRework: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const handleSubmit = async () => {
        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

        // Combine preLaminationEL and observationsEL
        const combinedPreLaminationEL = preLaminationEL.map((item, index) => ({
            product: item.product,
            specifications: item.specifications,
            frequency: item.frequency,
            observation1: observationsEL[index]?.observation1 || '',
            observation2: observationsEL[index]?.observation2 || '',
        }));

        // Combine reWork and observationsRework
        const combinedRework = reWork.map((item, index) => ({
            product: item.product,
            specifications: item.specifications,
            frequency: item.frequency,
            observation1: observationsRework[index]?.observation1 || '',
            observation2: observationsRework[index]?.observation2 || '',
        }));

        const payload = {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            status: 'complete',
            PreLamination: {
                PreLaminationELRework_pre: {
                    preLaminationEL: combinedPreLaminationEL,
                    reWork: combinedRework,
                    nonConformance,
                },
            },
        };

        try {
            await submitFormData(payload);
            alert('Data submitted successfully!');
        } catch (error) {
            console.error('Error submitting data:', error);
            alert('Failed to submit data.');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Pre-Lamination EL & Re-work Inspection</h3>

            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Pre-Lamination EL</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {preLaminationEL.map((item, index) => (
                            <tr key={index} className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}>
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observationsEL[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChangeEL(index, 'observation1', e.target.value)}
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observationsEL[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChangeEL(index, 'observation2', e.target.value)}
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Re-work</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reWork.map((item, index) => (
                            <tr key={index} className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}>
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observationsRework[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChangeRework(index, 'observation1', e.target.value)}
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observationsRework[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChangeRework(index, 'observation2', e.target.value)}
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2 text-gray-700">Non Conformance</h4>
                <textarea
                    value={nonConformance}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter details about non conformance..."
                    className="w-full border border-gray-300 p-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                    rows={4}
                />
            </div>

            <div className="mt-6">
                <button
                    onClick={handleSubmit}
                    className="bg-[#424752] text-white py-2 px-6 rounded-lg shadow-md hover:bg-gray-500 transition-all duration-150"
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default PreLaminationELRework;